import React, { Fragment, useState } from "react";
import { Container, Nav, NavDropdown, Navbar } from "react-bootstrap";
import { Link, NavLink, useNavigate } from "react-router-dom";

import { topBarLinks } from "./TopBar";

import logo from "../assets/images/mybl_logo.png";
import { authActions } from "../features/auth/authSlice";
import { useAppDispatch, useAppSelector } from "../hooks/reduxHooks";
import useBreakpoint from "../hooks/useBreakpoint";
import { AUTH_LEVELS } from "../lib/globals";
import { isXOrSmaller, validateToken } from "../lib/utils";
import { INavLinkType } from "../types/shared";

const Header = (): React.JSX.Element => {
    const [show, setShow] = useState<{ [key: string]: boolean }>({});
    const [navBarExpanded, setNavBarExpanded] = useState(false);
    const { user, loggedIn, token } = useAppSelector(state => state.auth);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const breakpoint = useBreakpoint();
    const toggleNavbar = () => setNavBarExpanded(!navBarExpanded);
    const shouldToggleNavbar = isXOrSmaller("md", breakpoint)
        ? toggleNavbar
        : undefined;

    const toggleDropdown = (title: string) => {
        setShow(prev => ({
            ...prev,
            [title]: !show[title],
        }));
    };

    const hideDropdown = (title: string) => {
        setShow(prev => ({
            ...prev,
            [title]: false,
        }));
    };

    const headerLinks: INavLinkType[] = [
        {
            title: "Field Locations",
            location: "/fields",
        },
        {
            title: "League Rules",
            location: "#",
            children: [
                {
                    title: "MAJOR YBL Rules",
                    location: "/rules/majorybl",
                },
                {
                    title: "MAJOR High School Rules",
                    location: "/rules/majorhighschool",
                },
            ],
        },
        {
            title: "Tryouts/Join a Team",
            location: "#",
            children: [
                {
                    title: "Tryouts Info",
                    location: "/tryouts",
                },
                {
                    title: "Teams looking for players",
                    location: "/looking-for-players",
                },
            ],
        },
        {
            title: "Contact Us",
            location: "/contact-us",
        },
        {
            title: "Register",
            location: "#",
            children: [
                {
                    title: "Team Registration",
                    location: "/register/team",
                },
                {
                    title: "Coach Registration",
                    location: "/register/coach",
                },
                {
                    title: "Organizational User Registration",
                    location: "/register/orguser",
                },
            ],
        },
        {
            title: "Coach's Clubhouse",
            location: "/coachs-clubhouse",
            requireAuth: true,
            authLevels: AUTH_LEVELS.clubhouse,
        },
        {
            title: "Organization Management",
            location: "/org-management",
            requireAuth: true,
            authLevels: AUTH_LEVELS.org,
        },
        {
            title: "League Management",
            location: "/league-management",
            requireAuth: true,
            authLevels: AUTH_LEVELS.leaguemanagement,
        },
        {
            title: "Site Management",
            location: "/site-management",
            requireAuth: true,
            authLevels: AUTH_LEVELS.sitemanagement,
        },
    ];

    const buildChildren = (children: INavLinkType[]): React.ReactNode => {
        return children.map(child => (
            <NavDropdown.Item
                as={Link}
                to={child.location}
                key={child.title}
                onClick={shouldToggleNavbar}
            >
                {child.title}
            </NavDropdown.Item>
        ));
    };

    const buildLinks = (links: INavLinkType[]) => {
        return links.map(
            (link: INavLinkType) =>
                (!link.requireAuth ||
                    (link.requireAuth &&
                        loggedIn &&
                        user &&
                        token &&
                        validateToken(token) &&
                        link.authLevels?.includes(user.userLevel))) && (
                    <Fragment key={link.title}>
                        {link.children ? (
                            <NavDropdown
                                title={link.title}
                                show={show[link.title]}
                                onClick={() => toggleDropdown(link.title)}
                                onMouseLeave={() => hideDropdown(link.title)}
                            >
                                {buildChildren(link.children)}
                            </NavDropdown>
                        ) : (
                            <Nav.Item>
                                <NavLink
                                    to={link.location}
                                    className="nav-link"
                                    onClick={shouldToggleNavbar}
                                >
                                    {link.title}
                                </NavLink>
                            </Nav.Item>
                        )}
                    </Fragment>
                ),
        );
    };

    return (
        <div id="header">
            <Container fluid="lg" className="header" data-testid="header">
                <Navbar expand="lg" className="nav" expanded={navBarExpanded}>
                    <NavLink className="nav-item" to="/">
                        <img src={logo} alt="MajorYBL.com Logo" />
                    </NavLink>
                    <Navbar.Toggle
                        aria-controls="basic-navbar-nav"
                        onClick={toggleNavbar}
                    />
                    <Navbar.Collapse>
                        <Nav>
                            {navBarExpanded && (
                                <>
                                    <Nav.Item>
                                        <NavLink
                                            to="/"
                                            className="nav-link"
                                            onClick={shouldToggleNavbar}
                                        >
                                            Home
                                        </NavLink>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <NavLink
                                            to="/leagues"
                                            className="nav-link"
                                            onClick={shouldToggleNavbar}
                                        >
                                            Leagues
                                        </NavLink>
                                    </Nav.Item>
                                </>
                            )}
                            {buildLinks(headerLinks)}
                            {navBarExpanded && (
                                <>
                                    <hr />
                                    {buildLinks(topBarLinks)}
                                    {user ? (
                                        <>
                                            <Nav.Item>
                                                <NavLink
                                                    to="/profile"
                                                    className="nav-link"
                                                    onClick={shouldToggleNavbar}
                                                >
                                                    My Profile
                                                </NavLink>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Link
                                                    to="#"
                                                    className="nav-link"
                                                    onClick={() => {
                                                        toggleNavbar();
                                                        dispatch(
                                                            authActions.logout(),
                                                        );
                                                        navigate("/");
                                                    }}
                                                >
                                                    Logout
                                                </Link>
                                            </Nav.Item>
                                        </>
                                    ) : (
                                        <Nav.Item>
                                            <NavLink
                                                to="/login"
                                                className={"nav-link login"}
                                                onClick={shouldToggleNavbar}
                                            >
                                                Login
                                            </NavLink>
                                        </Nav.Item>
                                    )}
                                </>
                            )}
                        </Nav>
                    </Navbar.Collapse>
                </Navbar>
            </Container>
        </div>
    );
};

export default Header;
